import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/** @type {Map<string, Element>} */
const quickAddContentCache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const quickAddPendingRequests = new Map();
/** @type {QuickAddComponent[]} */
const quickAddPreloadQueue = [];
/** @type {WeakSet<QuickAddComponent>} */
const queuedQuickAddComponents = new WeakSet();
const QUICK_ADD_PRELOAD_CONCURRENCY = 2;
let activeQuickAddPreloads = 0;
let quickAddPreloadScheduled = false;

function processQuickAddPreloadQueue() {
  while (activeQuickAddPreloads < QUICK_ADD_PRELOAD_CONCURRENCY && quickAddPreloadQueue.length > 0) {
    const component = quickAddPreloadQueue.shift();
    if (!component) return;

    queuedQuickAddComponents.delete(component);
    if (!component.isConnected) continue;

    activeQuickAddPreloads += 1;
    component.preloadQuickAdd().finally(() => {
      activeQuickAddPreloads -= 1;
      processQuickAddPreloadQueue();
    });
  }
}

function startQuickAddPreloading() {
  quickAddPreloadScheduled = false;

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(processQuickAddPreloadQueue, { timeout: 1500 });
  } else {
    window.setTimeout(processQuickAddPreloadQueue, 400);
  }
}

/** @param {QuickAddComponent} component */
function scheduleQuickAddPreload(component) {
  if (queuedQuickAddComponents.has(component)) return;

  queuedQuickAddComponents.add(component);
  quickAddPreloadQueue.push(component);

  if (quickAddPreloadScheduled) return;
  quickAddPreloadScheduled = true;

  if (document.readyState === 'complete') {
    startQuickAddPreloading();
  } else {
    window.addEventListener('load', startQuickAddPreloading, { once: true });
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    scheduleQuickAddPreload(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    quickAddContentCache.clear();
    quickAddPendingRequests.clear();
    scheduleQuickAddPreload(this);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const cachedProductGrid = quickAddContentCache.get(currentUrl);

    if (!cachedProductGrid) {
      this.#setLoadingState();
      this.#openQuickAddModal();
    }

    try {
      const productGrid = cachedProductGrid || (await this.#loadProductGrid(currentUrl));
      if (!productGrid) {
        this.#setErrorState();
        this.#openQuickAddModal();
        return;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      if (cachedProductGrid) {
        this.#openQuickAddModal();
      }
    } catch (error) {
      console.error('Unable to load quick add content', error);
      this.#setErrorState();
      this.#openQuickAddModal();
    }
  };

  prefetchQuickAdd = () => {
    const prefetch = () => {
      this.preloadQuickAdd();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prefetch, { timeout: 800 });
    } else {
      window.setTimeout(prefetch, 120);
    }
  };

  preloadQuickAdd = async () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || quickAddContentCache.has(currentUrl)) return;

    try {
      await this.#loadProductGrid(currentUrl);
    } catch (error) {
      console.warn('Unable to preload quick add content', error);
    }
  };

  /**
   * Loads and caches the product grid content used by the quick-add modal.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #loadProductGrid(productPageUrl) {
    if (!productPageUrl) return null;

    const cachedProductGrid = quickAddContentCache.get(productPageUrl);
    if (cachedProductGrid) return cachedProductGrid;

    const pendingRequest = quickAddPendingRequests.get(productPageUrl);
    if (pendingRequest) return pendingRequest;

    const request = this.fetchProductPage(productPageUrl)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        quickAddContentCache.set(productPageUrl, productGrid);

        return productGrid;
      })
      .finally(() => {
        quickAddPendingRequests.delete(productPageUrl);
      });

    quickAddPendingRequests.set(productPageUrl, request);

    return request;
  }

  #setLoadingState() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const loadingState = document.createElement('div');
    loadingState.className = 'quick-add-modal__loading';
    loadingState.setAttribute('role', 'status');
    loadingState.setAttribute('aria-live', 'polite');

    const statusText = document.createElement('span');
    statusText.className = 'visually-hidden';
    statusText.textContent = 'Loading options...';
    loadingState.appendChild(statusText);

    const mediaSkeleton = document.createElement('div');
    mediaSkeleton.className = 'quick-add-modal__skeleton-media quick-add-modal__skeleton-shimmer';
    mediaSkeleton.setAttribute('aria-hidden', 'true');
    loadingState.appendChild(mediaSkeleton);

    const detailsSkeleton = document.createElement('div');
    detailsSkeleton.className = 'quick-add-modal__skeleton-details';
    detailsSkeleton.setAttribute('aria-hidden', 'true');

    for (const className of [
      'quick-add-modal__skeleton-line quick-add-modal__skeleton-line--title',
      'quick-add-modal__skeleton-line quick-add-modal__skeleton-line--price',
      'quick-add-modal__skeleton-swatches',
      'quick-add-modal__skeleton-button',
    ]) {
      const skeletonItem = document.createElement('div');
      skeletonItem.className = `${className} quick-add-modal__skeleton-shimmer`;
      detailsSkeleton.appendChild(skeletonItem);
    }

    loadingState.appendChild(detailsSkeleton);

    modalContent.replaceChildren(loadingState);
  }

  #setErrorState() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const errorState = document.createElement('div');
    errorState.className = 'quick-add-modal__loading quick-add-modal__loading--error';
    errorState.setAttribute('role', 'status');
    errorState.textContent = 'Unable to load options. Please try again.';

    modalContent.replaceChildren(errorState);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    try {
      const response = await fetch(productPageUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const quickView = productGrid.querySelector('.tbh-quick-view');
    const variantPicker = productGrid.querySelector('variant-picker');

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    if (quickView && variantPicker) {
      const quickViewContainer = document.createElement('div');
      quickViewContainer.className = 'quick-add-modal__quick-view';
      quickViewContainer.appendChild(quickView.cloneNode(true));
      variantPicker.after(quickViewContainer);
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productVariantsCount = this.dataset.productVariantsCount;
    const quickAddButton = productVariantsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
